import React, { useState } from "react";
import { Box, Button, Typography, TextField } from "@mui/material";
import Modal from "../../components/Modal";
import PropTypes from "prop-types";
import makeStyles from "@mui/styles/makeStyles";
import { UploadButton } from "../../components/Buttons";
import { validateHttpsUrl } from "../../utils/validation";

const useStyles = makeStyles(theme => ({
  image: {
    height: 255,
    width: 255,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    [theme.breakpoints.only("sm")]: {
      height: 200,
      width: 200
    }
  },
  uploadButton: {
    marginLeft: theme.spacing(-1),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0
    }
  }
}));

export default function MyAppsModal(props) {
  const {
    id,
    title,
    open,
    onClose,
    onImgUpload,
    imgSrc,
    appNameLabel,
    appUrlLabel,
    onAppSave,
    onAppUpdate
  } = props;

  const classes = useStyles();

  const [appName, setAppName] = useState(props.appName);
  const [appUrl, setAppUrl] = useState(props.appUrl);
  const [appUrlErrorMsg, setAppUrlErrorMsg] = useState("");

  function handleAppNameChange(event) {
    setAppName(event.target.value);
  }
  function handleAppUrlChange(event) {
    let emailValidation = validateHttpsUrl(event.target.value);
    if (emailValidation) {
      setAppUrlErrorMsg("");
    } else {
      setAppUrlErrorMsg("Invalid URL.");
    }

    if (event.target.value.indexOf("https://") !== 0) {
      setAppUrlErrorMsg("URL should start with 'https://'.");
    }

    if (!event.target.value) {
      setAppUrlErrorMsg("URL is required.");
    }

    setAppUrl(event.target.value);
  }

  function onSaveClick() {
    let appObj = {
      name: appName,
      url: appUrl
    };

    if (id) {
      onAppUpdate(appObj, id);
    } else {
      onAppSave(appObj);
    }
  }

  return (
    <Modal
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {title}
        </Typography>
      }
      open={open}
      onClose={onClose}
      scrollable
      maxWidth={"lg"}
      dialogContent={
        <Box display="flex" flexDirection="column">
          <TextField
            id="appName"
            label={appNameLabel}
            type="text"
            variant="filled"
            margin="normal"
            value={appName}
            onChange={handleAppNameChange}
          />
          <TextField
            required
            id="appUrl"
            label={appUrlLabel}
            type="url"
            variant="filled"
            margin="normal"
            value={appUrl}
            error={appUrlErrorMsg !== ""}
            helperText={appUrlErrorMsg}
            onChange={handleAppUrlChange}
          />
          <Box
            display="flex"
            mt={4}
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="center"
          >
            <div
              style={{ backgroundImage: `url("${imgSrc}")` }}
              className={classes.image}
            />
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems={{ xs: "center", sm: "flex-start" }}
              ml={{ xs: 0, sm: 5 }}
              padding={1}
            >
              <UploadButton
                color="primary"
                buttonClassName={classes.uploadButton}
                labelVariant="inherit"
                buttonVariant="text"
                buttonText="Upload App Image or Icon"
                acceptType="image/*"
                onUpload={onImgUpload}
                buttonId="upload-profile-image-button"
              />
              <Typography>Image size: 2048 x 2048 px max</Typography>
            </Box>
          </Box>
        </Box>
      }
      dialogActions={
        <>
          <Button color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSaveClick}
            disabled={appUrlErrorMsg !== "" || appUrl === ""}
            color="primary"
          >
            Save
          </Button>
        </>
      }
    />
  );
}

MyAppsModal.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onImgUpload: PropTypes.func,
  onAppSave: PropTypes.func,
  onAppUpdate: PropTypes.func,
  appName: PropTypes.string,
  appUrl: PropTypes.string,
  appNameLabel: PropTypes.string,
  appUrlLabel: PropTypes.string
};

MyAppsModal.defaultProps = {
  appNameLabel: "App Name",
  appUrlLabel: "App URL https://",
  appName: "",
  appUrl: "",
  id: null
};
